// The exact MetaHuman rig in the browser: OpenRigLogic (Epic) compiled to wasm
// evaluates controls -> joint transforms + blendshape weights, and deform.js
// applies the blendshapes + linear-blend skinning to produce vertices.
//
// Its consumer is src/ogs/graph/nodes/OrlDeform — the OGS/texavatars branch's
// rig2mesh.
//
// Both correctness gates hold:
// the wasm output is bit-identical to native ORL, and the deform reproduces an
// independent numpy reference to 1e-3 cm (tests/orl/).
//
// Units: the DNA and the baked bundle are in CENTIMETRES. This module returns cm
// — callers convert at their own boundary.

import { createDeformer } from "./deform.js";
import { ORL_PACK_FILE, ORL_REQUIRED_MEMBERS, orlAssetName } from "./bundleFiles.js";
import { parseOrlPack } from "./orlPack.js";
import createRigLogicModule from "./riglogic.js";

/** Identity-INDEPENDENT wasm code — the one ORL artifact that ships with the app
 *  rather than with a character. Every baked rig is character data and comes
 *  from that character's assets.
 *
 *  CONTENT-HASHED, deliberately. It sits next to its glue in src/ instead of in
 *  public/ so the bundler emits it as /assets/riglogic-<hash>.wasm: a re-bake
 *  changes the bytes, so it changes the URL, so every cache keyed on that URL
 *  misses. Under public/ it was copied verbatim and unhashed, which made
 *  freshness a MANUAL lever (staticAssetCache's STATIC_ASSET_VERSION) — and the
 *  failure mode for forgetting it is the worst kind: the content-hashed glue
 *  updates itself and then calls into a stale binary missing the exports it
 *  expects, giving a dead rig recoverable only through Clear Cache.
 *
 *  new URL(..., import.meta.url) rather than an `?url` import because Node
 *  understands it natively — the bake tools and the ORL test gates import this
 *  module outside any bundler and would fail to resolve `?url`. */
export const ORL_WASM_URL = new URL("./riglogic.wasm", import.meta.url).href;

/** Where the behaviour DNA is staged for RigLogic's stream reader. */
const DNA_MEMFS_PATH = "/head.dna";

/** Build the deformer from a parsed pack. */
function deformerFromPack(pack) {
  const need = (file) => {
    const b = pack.get(file);
    if (!b) throw new Error(`[orl] pack has no "${file}" (carries: ${pack.names.join(", ")})`);
    return b;
  };
  // createDeformer wants ArrayBuffers; pack members are Uint8Array views.
  const ab = (file) => {
    const b = need(file);
    return b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength);
  };
  const manifest = pack.json("manifest.json");
  const bsTargets = pack.json("bs_targets.json");
  const bufs = {};
  for (const f of ORL_REQUIRED_MEMBERS) {
    if (f.endsWith(".bin")) bufs[f.replace(/\.bin$/, "")] = ab(f);
  }
  return { deformer: createDeformer(manifest, bufs, bsTargets), manifest, bsTargets };
}

// MetaHuman control names are "CTRL_C_jaw.translateY"; DNA GUI names are
// "CTRL_C_jaw.ty".
export function mhNameToDna(name) {
  return name.replace(/\.translate([XYZ])$/, (_, axis) => "." + "t" + axis.toLowerCase());
}

/**
 * WHICH OF RIGLOGIC'S TWO CONTROL LAYERS a name list is written in.
 *
 * Every MetaHuman DNA carries both, and they are disjoint NAMESPACES rather
 * than two spellings of one thing (measured on isaac's DNA):
 *
 *   GUI  174 names over 161 widgets  `CTRL_L_brow_down.ty`         the animator rig
 *   RAW  263 names                   `CTRL_expressions.browDownL`  what RigLogic evaluates
 *                                    (251 expressions + 12 head/neck pose quaternions)
 *
 * `mapGUIToRawControls` bridges GUI -> RAW through conditional tables and does
 * NOT invert, so a list is in one layer or the other and matching across them
 * resolves ZERO. Every bundle this app ships today is GUI — which is why
 * `buildControlMap` alone has been sufficient — but a bundle solved against the
 * raw layer resolves 0 of 263 through it, and the error that produces blames
 * the DNA for having diverged when the DNA is correct and the namespaces simply
 * do not overlap. That misdiagnosis is the whole reason this exists.
 *
 * Decided by COUNTING MATCHES in each layer, not by a flag: the names are
 * already unambiguous, and a flag is one more thing that can disagree with the
 * file it describes.
 */
export function detectControlSpace(inputNames, guiNames, rawNames) {
  const raw = new Set(rawNames);
  const gui = new Set(guiNames);
  let nRaw = 0, nGui = 0;
  for (const n of inputNames) {
    if (raw.has(n)) nRaw++;
    if (gui.has(mhNameToDna(n))) nGui++;
  }
  return { space: nRaw > nGui ? "raw" : "gui", nRaw, nGui };
}

/**
 * Input control index -> DNA RAW control index, for a list already written in
 * the raw layer.
 *
 * Separate from `buildControlMap` because the failure it guards is different: a
 * GUI list legitimately leaves ~11 controls unmatched (eye aim, lipsPressD, the
 * viewport switches), while a raw list either IS this DNA's raw set or belongs
 * to another character entirely.
 */
export function buildRawControlMap(inputNames, rawNames, { minMatched = 1 } = {}) {
  const rawIndex = new Map(rawNames.map((n, i) => [n, i]));
  const rawForInput = new Int32Array(inputNames.length).fill(-1);
  const unmatched = [];
  for (let i = 0; i < inputNames.length; i++) {
    const r = rawIndex.get(inputNames[i]);
    if (r === undefined) unmatched.push(inputNames[i]);
    else rawForInput[i] = r;
  }
  const matched = inputNames.length - unmatched.length;
  if (matched < minMatched) {
    throw new Error(
      `[orl] raw control map: only ${matched}/${inputNames.length} controls resolved against ` +
        `the DNA's ${rawNames.length} RAW controls (expected >= ${minMatched}) — wrong DNA for this bundle`,
    );
  }
  return { rawForInput, matched, unmatched };
}

/**
 * Map an arbitrary control-name list (MetaHuman naming) onto the DNA's GUI
 * control indices.
 *
 * Returns `guiForInput[i]` = the DNA GUI index driven by input control `i`, or
 * -1 when that control has no counterpart in this DNA. Unmatched controls are
 * NOT an error — real bundles legitimately carry controls RigLogic doesn't
 * expose (eye aim, lipsPressD, viewport switches) — but a caller that matches
 * almost nothing has a real problem, so `minMatched` throws.
 */
export function buildControlMap(inputNames, guiNames, { minMatched = 1 } = {}) {
  const guiIndex = new Map(guiNames.map((n, i) => [n, i]));
  const guiForInput = new Int32Array(inputNames.length).fill(-1);
  const unmatched = [];
  for (let i = 0; i < inputNames.length; i++) {
    const g = guiIndex.get(mhNameToDna(inputNames[i]));
    if (g === undefined) unmatched.push(inputNames[i]);
    else guiForInput[i] = g;
  }
  const matched = inputNames.length - unmatched.length;
  if (matched < minMatched) {
    throw new Error(
      `[orl] control map regressed: only ${matched}/${inputNames.length} controls resolved ` +
        `against the DNA's ${guiNames.length} GUI controls (expected >= ${minMatched}). ` +
        `The DNA and the control-name list have diverged — the face would barely move.`,
    );
  }
  return { guiForInput, matched, unmatched };
}

/**
 * Boot the wasm rig + geometry bundle and return a synchronous evaluator.
 *
 * The pack comes from already-mounted CHARACTER assets (`getBytes`, used by the
 * OGS branch — the rig rides with the character). There is deliberately no
 * static-URL source: a baked rig is character data and never ships in public/.
 *
 * SHARED PER PACK, refcounted — `release()` is paired with this call, not
 * optional. A multi-region bundle asks twice (OrlDeform skins head_lod0,
 * OrlShellDeform drives the eyes/teeth shells), and building it twice means two
 * wasm instantiations, two DNAs in two MEMFS heaps, and two dequantizations of
 * ~2.4 M int8 deltas into a ~28 MB array the shell deform never reads. Sharing
 * is also what makes teardown expressible: with one owner per instance nobody
 * could say when the module was free, so the heap survived every character
 * switch.
 *
 * Keyed on the pack BYTES (identity, not content): one VFS hands every branch
 * the same Uint8Array, so same-object means same rig by construction.
 *
 * @param {object} opts
 * @param {(name: string) => Uint8Array | undefined} opts.getBytes mounted `orl_*` assets
 * @param {string[]} opts.controlNames control names, in the order the caller will supply values
 * @param {string}   [opts.wasmUrl]
 * @param {number}   [opts.expectVerts] assert the bundle's vertex count (loud mismatch)
 * @param {number}   [opts.minMatched]
 * @param {(m:string)=>void} [opts.log]
 * @param {Function} [opts.createModule] riglogic module factory; an option for the
 *   same reason dnaToPack.js has one — it is what lets the sharing and teardown
 *   rules above be tested without a wasm build or a character's rig.
 */
export async function createOrlRig({
  getBytes,
  controlNames,
  wasmUrl = ORL_WASM_URL,
  expectVerts = 0,
  minMatched = 1,
  log = () => {},
  createModule = createRigLogicModule,
}) {
  if (!getBytes) throw new Error("[orl] createOrlRig needs getBytes");

  // ONE fetch / ONE mounted asset for the whole rig — DNA, mesh, weights and
  // blendshapes all ride in the same pack, so there is no partially-loaded
  // state to reason about.
  const packName = orlAssetName(ORL_PACK_FILE);
  const packSource = `character assets (${packName})`;
  const packBytes = getBytes(packName);
  if (!packBytes) throw new Error(`[orl] ${packName} is not mounted`);

  // The IN-FLIGHT promise is cached, not just the result: the two branches are
  // built back to back and both awaits land before either resolves, so caching
  // only on completion would still build two.
  let shared = SHARED.get(packBytes);
  if (!shared) {
    shared = { refs: 0, rig: buildOrlRig({ packBytes, packSource, controlNames, wasmUrl, minMatched, log, createModule }) };
    SHARED.set(packBytes, shared);
  }
  shared.refs++;
  let base;
  try {
    base = await shared.rig;
  } catch (e) {
    // A failed build must not stay cached as a permanently-rejecting promise: the
    // branch's own fallback re-enters here, and every retry for the life of the
    // page would replay the first error.
    if (--shared.refs <= 0) SHARED.delete(packBytes);
    throw e;
  }
  // The cache is keyed on the PACK, but the control map is built from the FIRST
  // caller's controlNames — so a second branch supplying a different order would
  // silently write its values through someone else's GUI indices, and (since
  // evaluateRig memoizes on the vector) could even be handed the other branch's
  // outputs verbatim. In practice both branches of a bundle pass the same
  // rig_names.json; this makes that a checked precondition rather than a
  // coincidence, because the failure is a subtly wrong face, not an error.
  if (base.controlNames.length !== controlNames.length ||
      base.controlNames.some((n, i) => n !== controlNames[i])) {
    releaseShared(packBytes);
    throw new Error(
      `[orl] ${packName} is already shared with a branch using a different control order ` +
        `(${base.controlNames.length} vs ${controlNames.length} names) — one pack serves one control space`,
    );
  }
  if (expectVerts && base.deformer.V !== expectVerts) {
    releaseShared(packBytes);
    throw new Error(
      `[orl] bundle vertex count ${base.deformer.V} != expected ${expectVerts} (${packSource}) — wrong identity or wrong mesh`,
    );
  }
  let released = false;
  return {
    ...base,
    /** Drop this holder's claim; the module goes when the last one does.
     *  Idempotent — a double dispose must not free a sibling's live rig. */
    release() {
      if (released) return;
      released = true;
      releaseShared(packBytes);
    },
  };
}

/** packBytes -> { refs, rig: Promise<rig> }. Keyed by object identity, so it
 *  holds no character alive on its own once every branch has released. */
const SHARED = new Map();

function releaseShared(packBytes) {
  const shared = SHARED.get(packBytes);
  if (!shared || --shared.refs > 0) return;
  SHARED.delete(packBytes);
  // Settled by construction — nothing reaches here without having awaited it.
  shared.rig.then((rig) => rig.destroy()).catch(() => { /* a failed build owns nothing */ });
}

async function buildOrlRig({ packBytes, packSource, controlNames, wasmUrl, minMatched, log, createModule }) {
  const pack = parseOrlPack(packBytes);
  const missing = ORL_REQUIRED_MEMBERS.filter((f) => !pack.has(f));
  if (missing.length) throw new Error(`[orl] pack from ${packSource} is missing: ${missing.join(", ")}`);

  const M = await createModule({
    locateFile: (p) => (p === "riglogic.wasm" ? wasmUrl : p),
  });

  // RigLogic reads the DNA through its own stream layer, so hand it a file in
  // MEMFS: loadDNA() marshals via convertJSArrayToNumberVector and is markedly
  // slower for a multi-MB rig.
  const dnaSource = packSource;
  const dnaBytes = pack.get("head_behavior.dna");
  M.FS.writeFile(DNA_MEMFS_PATH, dnaBytes);
  if (!M.loadDNAFromPath(DNA_MEMFS_PATH)) {
    throw new Error(`[orl] loadDNAFromPath failed — the DNA from ${dnaSource} is unreadable`);
  }

  const guiNames = M.guiControlNames();
  // A wasm predating the raw exports still drives everything above; only the
  // readout and the raw drive path are unavailable, and both check by name.
  const rawNames = typeof M.rawControlNames === "function" ? M.rawControlNames() : [];

  // Which layer this bundle's rig_names.json is written in. Every bundle shipped
  // so far is GUI; a raw one resolves 0 controls through the GUI map, and the
  // error that produces blames the DNA. See detectControlSpace.
  const { space, nRaw, nGui } = detectControlSpace(controlNames, guiNames, rawNames);
  let guiForInput = new Int32Array(0), rawForInput = new Int32Array(0), matched = 0, unmatched = [];
  // A list should score in ONE layer — the namespaces are disjoint, so the loser
  // normally scores zero. Both scoring means the names are mixed, and the choice
  // below is then a majority vote on something that should not have been a vote:
  // whichever layer loses, those controls silently go unmapped. Say so; the
  // counts are the whole diagnosis.
  if (nRaw > 0 && nGui > 0) {
    log(
      `[orl] WARNING: rig_names matches BOTH control layers (${nGui} GUI, ${nRaw} RAW) — ` +
        `driving as ${space.toUpperCase()}, so the other ${space === "raw" ? nGui : nRaw} will not map`,
    );
  }
  if (space === "raw") {
    ({ rawForInput, matched, unmatched } = buildRawControlMap(controlNames, rawNames, { minMatched }));
  } else {
    ({ guiForInput, matched, unmatched } = buildControlMap(controlNames, guiNames, { minMatched }));
  }

  const { deformer, manifest } = deformerFromPack(pack);
  /** The last control vector solved, and the object evaluateRig hands its views
   *  back in — see evaluateRig for why the arrays themselves are not cached. */
  let lastControls = null;
  const out = { jointOut: null, bsOut: null };
  log(
    `[orl] character assets: ${deformer.V} verts, ${deformer.J} joints, ${manifest.numBlendShapeTargets} bs targets, ` +
      `${matched}/${controlNames.length} ${space.toUpperCase()} controls mapped` +
      (unmatched.length ? ` (unmapped: ${unmatched.join(", ")})` : ""),
  );

  /**
   * controls -> posed vertices, in CENTIMETRES.
   *
   * Synchronous — there is no inference call to await. Returns the deformer's
   * internal scratch buffer, which is overwritten on the next call; copy it if
   * you need to retain it. (Same contract as ORT's reused output buffer.)
   *
   * Values pass through SIGNED: several MetaHuman controls are differences of
   * ARKit pairs and their DNA counterparts are genuinely bipolar (CTRL_C_jaw.tx
   * deforms symmetrically at +1 and -1), so clamping to [0,1] would silently
   * kill half of every such control's range.
   */
  function evaluate(controls) {
    const { jointOut, bsOut } = evaluateRig(controls);
    return deformer.deform(jointOut, bsOut);
  }

  /**
   * The rig half only: controls -> joint transforms + blendshape weights, with
   * no deform. This is what the GPU path uses — it uploads these two small
   * arrays and does the per-vertex work in WGSL.
   *
   * Values pass through SIGNED (see above).
   */
  function evaluateRig(controls) {
    // MEMOIZED ON THE CONTROL VECTOR, because the rig is SHARED and a
    // multi-region bundle evaluates it TWICE per frame from one input: OrlDeform
    // skins head_lod0 and OrlShellDeform poses the eyes/teeth shells, both off the
    // control vector their branch was handed. Without this, mapGUIToRawControls +
    // calculate — the whole 870-joint solve — runs a second time for a pose that
    // has already been computed.
    //
    // Only the SOLVE is memoized, not the arrays it produced. The getters hand
    // back zero-copy views onto the wasm heap, which are valid only until the
    // next calculate() and are DETACHED outright if the heap grows
    // (-sALLOW_MEMORY_GROWTH), so caching one across frames would eventually
    // hand a branch a zero-length array. Re-reading them is two view
    // constructions and no copy; the 870-joint solve is what was worth skipping.
    const hit = lastControls !== null && sameControls(controls);
    if (!hit) {
      if (space === "raw") {
        // Straight into the layer RigLogic evaluates. NO mapGUIToRawControls
        // after this: that call writes GUI -> RAW and would overwrite
        // everything just set, leaving the face at whatever the (untouched) GUI
        // controls say — which is neutral.
        for (let i = 0; i < rawForInput.length; i++) {
          const r = rawForInput[i];
          if (r >= 0) M.setRawControl(r, controls[i]);
        }
      } else {
        for (let i = 0; i < guiForInput.length; i++) {
          const g = guiForInput[i];
          if (g >= 0) M.setGUIControl(g, controls[i]);
        }
        M.mapGUIToRawControls(); // REQUIRED — skipping it silently yields all-zero output
      }
      M.calculate();
      // COPIED, not aliased: the caller owns `controls` and may refill the same
      // buffer next frame, which would leave the comparison checking a vector
      // against itself and pin the face on one pose forever.
      if (lastControls === null || lastControls.length !== controls.length) {
        lastControls = new Float32Array(controls.length);
      }
      lastControls.set(controls);
    }
    // Reused across calls so a hit allocates nothing at all — the caller reads
    // both fields immediately and never keeps the object.
    out.jointOut = M.getJointOutputs();
    out.bsOut = M.getBlendShapeOutputs();
    return out;
  }

  /**
   * A control vector -> the RAW activations RigLogic derives from it.
   *
   * The GUI layer is ERGONOMIC; the raw layer is what the rig evaluates and what
   * a SOMA-X-solved model is conditioned on. This is the correspondence between
   * them, performed by RigLogic through the conditional tables that define it
   * rather than reimplemented on this side.
   *
   * WHAT THE MAPPING ACTUALLY DOES, measured on a real head.dna rather than
   * assumed from the fact that it runs through conditional tables:
   *
   *   - it is a SIGNED PERMUTATION. A single GUI edit lights EXACTLY ONE raw
   *     control (243 of 348 control x sign probes) or none (105); never two. So
   *     do not describe this as one widget fanning out across several raw
   *     expressions — on this DNA it does not.
   *   - it is EXACTLY ADDITIVE. All 174 controls set at once reproduces the sum
   *     of the individual edits to 0.0000, lighting 177 raw controls — the 3
   *     extra being RigLogic's own interpolation stages.
   *   - every GUI control works on its POSITIVE side (0 of 174 dead at +1). 103
   *     are inert at -1, which is simply what unipolar means, and exactly one
   *     (`CTRL_C_neck_swallow.ty`) is inert both ways because its raw
   *     counterparts are driven internally.
   *
   * The value here is therefore the CORRESPONDENCE — which raw control a slider
   * is, in the namespace the model is conditioned on — not watching tables
   * combine.
   *
   * DELIBERATELY NOT ON THE PER-FRAME PATH. `evaluateRig` already runs this
   * mapping and could hand the vector back for free, but that would put a copy
   * (getRawControlValues copies, unlike the two output getters) into every
   * posed frame to serve a panel that reads at UI cadence. This is a separate
   * call, on user input only: one mapping pass, no `calculate()`, microseconds.
   *
   * Because it resets and rewrites the GUI controls, it CLOBBERS the rig's
   * current pose — so it also invalidates the memo, or the next `evaluateRig`
   * for the same vector would return a hit computed from controls that are no
   * longer set. Callers get the raw vector; the next real pose re-solves.
   *
   * Returns null on a wasm without the raw exports (an older riglogic.wasm),
   * rather than an empty array a caller would render as "nothing active".
   */
  function guiToRaw(controls) {
    if (typeof M.getRawControlValues !== "function") return null;
    if (space === "raw") {
      // Already raw: there is nothing to convert, and running the GUI mapping
      // would report what the (unset) GUI layer maps to, i.e. nothing.
      const outRaw = new Float32Array(rawNames.length);
      for (let i = 0; i < rawForInput.length; i++) {
        const r = rawForInput[i];
        if (r >= 0) outRaw[r] = controls[i];
      }
      return outRaw;
    }
    M.resetGUIControls();
    for (let i = 0; i < guiForInput.length; i++) {
      const g = guiForInput[i];
      if (g >= 0) M.setGUIControl(g, controls[i]);
    }
    M.mapGUIToRawControls();
    lastControls = null; // the pose above is gone — see the note on clobbering
    // COPIED: getRawControlValues returns a view onto the wasm heap, and the
    // next mapping overwrites it in place.
    return Float32Array.from(M.getRawControlValues());
  }

  /**
   * RAW -> GUI: the direction RigLogic does not provide.
   *
   * MEASURED, not assumed. `guiToRaw` above records the shape: the mapping is a
   * signed permutation, so its inverse is an INDEX LOOKUP WITH A SIGN rather
   * than a solve. Probing this DNA's own mapping (each GUI control at +1 and at
   * -1) builds that lookup from the rig itself, so it stays correct for a DNA
   * whose tables differ.
   *
   * PROBING BOTH SIGNS IS WHAT MAKES IT WORK, and is the easy thing to get
   * wrong. 103 of 174 controls are inert at -1 — but the other 70 are the
   * two-axis widgets, whose negative half maps to DIFFERENT raw controls (left
   * vs right, up vs down). Sampling only +1 and concluding the rest do not
   * exist loses those 70 outright.
   *
   * IT IS AN APPROXIMATION, and callers that surface it should say so. Two
   * residuals, both measured:
   *
   *   - 20 of 263 raw controls have NO GUI source — RigLogic's own
   *     interpolation stages (mouthLipsSticky*, neckSwallow*) and the head/neck
   *     pose quaternions. They have no slider to land on and are dropped.
   *   - a control whose transfer is NON-LINEAR IN VALUE does not invert from a
   *     +-1 probe. On this DNA that is the sticky-lip pair, whose transfer is a
   *     step rather than a ramp: at 0.5 it routes to a DIFFERENT raw control
   *     than at 1, so the lookup built at +-1 does not describe it and it comes
   *     back as zero. Everything else round-trips exactly (1e-5 over 262 of 263
   *     raw controls), which the gate in tests/orl/orlDeform.test.mjs asserts —
   *     and it separates the two cases by re-probing each failure for
   *     linearity, so a LINEAR control that stops inverting fails the build
   *     instead of being absorbed into "it is an approximation".
   *
   * NO UI CONSUMER TODAY: every bundle we ship is written in the GUI layer, so
   * its pose rows already live in the board's own space and need no conversion.
   * This exists as the other half of the pair for the bundle that isn't — a
   * raw-conditioned one, whose 263 activations are not a control surface anyone
   * wants to pose by hand. `detectControlSpace` is what will route to it.
   *
   * Built once, lazily: 2 x guiCount mapping passes and no `calculate()`, which
   * is microseconds — but only a caller wanting this direction should pay it.
   * Like `guiToRaw` it CLOBBERS the rig's pose, so it invalidates the memo.
   */
  let rawToGuiMap = null;
  function buildRawToGui() {
    const probe = (j, v) => {
      M.resetGUIControls();
      if (j >= 0) M.setGUIControl(j, v);
      M.mapGUIToRawControls();
      return Float32Array.from(M.getRawControlValues());
    };
    const zero = probe(-1, 0);
    /** raw index -> [gui index, sign, gain] */
    const map = new Map();
    for (let j = 0; j < guiNames.length; j++) {
      for (const sign of [1, -1]) {
        const col = probe(j, sign);
        for (let i = 0; i < col.length; i++) {
          const d = col[i] - zero[i];
          // First writer wins: a raw control driven from two GUI sources would
          // make the inverse ambiguous, and the measurement says none is.
          if (Math.abs(d) > 1e-6 && !map.has(i)) map.set(i, [j, sign, d]);
        }
      }
    }
    M.resetGUIControls();
    return map;
  }

  /** A raw activation vector as GUI control values, or null on a wasm without
   *  the raw exports. Unreachable raw controls are dropped — see above. */
  function rawToGui(raw) {
    if (typeof M.getRawControlValues !== "function") return null;
    if (!rawToGuiMap) rawToGuiMap = buildRawToGui();
    const gui = new Float32Array(guiNames.length);
    for (const [i, [j, sign, gain]] of rawToGuiMap) {
      const v = raw[i];
      if (v) gui[j] += (sign * v) / gain;
    }
    lastControls = null; // the probes above left the rig somewhere else
    return gui;
  }

  /**
   * A raw activation vector as THIS BUNDLE'S control vector — the one the board
   * drives and the decoders are conditioned on.
   *
   * NOT the same thing as `rawToGui`, and the difference is load-bearing:
   * `rawToGui` is indexed by DNA GUI index, while a bundle's `rig_names.json` is
   * its own order and its own width (188 MetaHuman names against this DNA's 174
   * GUI controls, 14 of which resolve to nothing). Feeding one into the other
   * index-for-index poses the wrong controls, and plausibly — every value is in
   * range and the face simply moves wrongly.
   *
   * This is what makes RAW EDITABLE. The model takes the bundle vector, so a raw
   * edit has to come back through here; driving RigLogic's raw controls directly
   * would move the MESH while the appearance decoder still read the unchanged
   * bundle vector, and the two would silently disagree.
   */
  function rawToControls(raw) {
    if (space === "raw") {
      // The bundle already speaks raw — a straight scatter, no inverse needed,
      // and none of rawToGui's approximation applies.
      const outRaw = new Float32Array(controlNames.length);
      for (let i = 0; i < rawForInput.length; i++) {
        const r = rawForInput[i];
        if (r >= 0) outRaw[i] = raw[r];
      }
      return outRaw;
    }
    const gui = rawToGui(raw);
    if (!gui) return null;
    const out = new Float32Array(controlNames.length);
    for (let i = 0; i < guiForInput.length; i++) {
      const g = guiForInput[i];
      if (g >= 0) out[i] = gui[g];
    }
    return out;
  }

  /**
   * Which raw controls an edit can actually reach: `true` where the inverse has
   * a GUI source to route through.
   *
   * A UI has to know. 20 of 263 have no source — RigLogic's own interpolation
   * stages and the head/neck quaternions — so a slider for one of those would
   * move and change nothing, which reads as a broken rig rather than as a
   * control that is derived rather than driven.
   */
  function drivableRaw() {
    if (space === "raw") return rawNames.map((_, i) => rawForInput.includes(i));
    if (typeof M.getRawControlValues !== "function") return rawNames.map(() => false);
    if (!rawToGuiMap) rawToGuiMap = buildRawToGui();
    return rawNames.map((_, i) => rawToGuiMap.has(i));
  }

  /** True when `controls` matches the vector the cached outputs came from. NaN
   *  never equals itself, so a NaN forces a re-solve rather than pinning the face
   *  on the last good pose. */
  function sameControls(controls) {
    if (controls.length !== lastControls.length) return false;
    for (let i = 0; i < controls.length; i++) {
      if (controls[i] !== lastControls[i]) return false;
    }
    return true;
  }

  /** Free the wasm heap. Called by releaseShared() when the last holder lets go,
   *  never by a branch directly — which is why it is not on the returned object. */
  function destroy() {
    try { M.FS.unlink(DNA_MEMFS_PATH); } catch { /* already gone */ }
  }

  return {
    evaluate,
    evaluateRig,
    guiToRaw,
    rawToGui,
    rawToControls,
    drivableRaw,
    /** "gui" | "raw" — which layer this bundle's control vector is written in. */
    controlSpace: space,
    /** The DNA's RAW control names, or [] on a wasm without the raw exports. */
    rawNames,
    deformer,
    V: deformer.V,
    J: deformer.J,
    neutral: deformer.neutral, // cm
    guiNames,
    controlNames,
    guiForInput,
    matched,
    unmatched,
    manifest,
    destroy,
  };
}
